/**
 * Google Ads landing page (/first-chapter) admin API client.
 *
 * Same-origin, cookie auth — nginx forwards /api/* to the admin backend.
 *
 * Unlike the older website-analytics endpoint, this API speaks the canonical
 * range tokens (today | 3d | 7d | 14d | 30d | 90d | custom + from/to), so there
 * is no numeric-string dialect to translate.
 *
 * node-pg hands back bigint/numeric as strings, so every count and money field
 * is typed `number | string` and must be read through `num()`.
 */

import { TimeRangeValue, resolveRangeYmd, toApiToken } from '../common/timeRange';

const BASE = '/api/admin/google-ads-landing';

export class GoogleAdsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'GoogleAdsApiError';
    this.status = status;
  }
}

/** Postgres bigint/numeric arrive as strings. */
export function num(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function rangeQuery(value: TimeRangeValue): string {
  if (value.type === 'custom') {
    const { from, to } = resolveRangeYmd(value);
    return `range=custom&from=${from}&to=${to}`;
  }
  return `range=${toApiToken(value)}`;
}

async function request<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  let body: { success?: boolean; data?: T; error?: string } = {};
  try {
    body = await res.json();
  } catch {
    throw new GoogleAdsApiError(`Malformed response (${res.status})`, res.status);
  }
  if (!res.ok || body?.success === false) {
    throw new GoogleAdsApiError(body?.error || `Request failed (${res.status})`, res.status);
  }
  return (body.data !== undefined ? body.data : ({} as T));
}

/** Transparency block: what window was actually queried, and was it clamped. */
export interface RangeMeta {
  landing_epoch: string;
  requested_from: string | null;
  requested_to: string | null;
  effective_from: string;
  effective_to: string;
  epoch_clamped: boolean;
}

export interface AdsCost {
  connected: boolean;
  cost_usd: number | string | null;
  impressions: number | string | null;
  clicks: number | string | null;
  source: string;
  /** True when weekly readings were spread over the days of the window. */
  prorated?: boolean;
  weeks_used?: number;
  note?: string | null;
  not_connected_reason: string | null;
}

export interface SummaryResponse {
  range: string;
  range_meta: RangeMeta;
  generated_at: string;
  landings: {
    total: number | string;
    /** Paid subset (utm_source google / utm_medium cpc|ppc|paid). Compare THIS
     *  against ad clicks — total includes organic, direct and referral. */
    paid: number | string;
    organic_and_other: number | string;
    mobile: number | string;
    tablet: number | string;
    desktop: number | string;
    avg_time_sec: number | null;
    avg_scroll_pct: number | null;
  };
  store_clicks: {
    total: number | string;
    ios: number | string;
    android: number | string;
    unique_sessions: number | string;
  };
  cta_clicks_total: number | string;
  ads: AdsCost;
  /** Every ratio is null — never 0, never Infinity — when its denominator is 0. */
  ratios: {
    click_delivery_rate: number | null;
    landing_to_store_ctr: number | null;
    cost_per_landing: number | null;
    cost_per_store_click: number | null;
    ios_share: number | null;
    android_share: number | null;
  };
  attributed_signups: { available: false; reason: string; unlock: string };
}

export interface TimeseriesPoint {
  bucket: string;
  landings: number;
  paid_landings: number;
  store_clicks: number;
  ios: number;
  android: number;
  cost_usd: number;
  ad_clicks: number;
}

export interface TimeseriesResponse {
  range: string;
  range_meta: RangeMeta;
  generated_at: string;
  series: TimeseriesPoint[];
  /** How the spend line was derived (weekly readings, spread evenly across each week). */
  cost_source?: string;
}

export interface CtaRow {
  cta_id: string;
  cta_label: string | null;
  clicks: number | string;
  unique_clicks: number | string;
  pct_of_total: number | null;
}

export interface CtaBreakdownResponse {
  range: string;
  range_meta: RangeMeta;
  generated_at: string;
  note: string;
  rows: CtaRow[];
}

export interface UtmRow {
  utm_campaign: string;
  utm_source: string;
  utm_medium: string;
  utm_term: string;
  utm_content: string;
  ios: number | string;
  android: number | string;
  total: number | string;
}

export interface UtmBreakdownResponse {
  range: string;
  range_meta: RangeMeta;
  generated_at: string;
  rows: UtmRow[];
}

export type CampaignTypeKey = 'search' | 'pmax' | 'other';

export interface CampaignTrendWeek {
  friday: string;
  spend: number;
  partial: boolean;
}

export interface CampaignRow {
  name: string;
  type: CampaignTypeKey;
  type_label: string;
  /** False when the campaign has no reading in the newest week (paused or idle). */
  active_latest_week: boolean;
  /** Newest-week figures; null, not 0, when the campaign has no row that week. */
  spend: number | null;
  clicks: number | null;
  conv: number | null;
  impr: number | null;
  /** Null when there were no conversions: unknown, never 0 or Infinity. */
  cost_per_conv: number | null;
  partial: boolean;
  /** Four weeks of spend, oldest first, ending on the newest week. */
  trend: CampaignTrendWeek[];
}

export interface CampaignsResponse {
  generated_at: string;
  source: string;
  latest_week: { friday: string; label: string | null; partial: boolean } | null;
  trend_weeks?: string[];
  campaigns: CampaignRow[];
}

export const googleAdsLandingApi = {
  getSummary:      (r: TimeRangeValue) => request<SummaryResponse>(`/summary?${rangeQuery(r)}`),
  getTimeseries:   (r: TimeRangeValue) => request<TimeseriesResponse>(`/timeseries?${rangeQuery(r)}`),
  getCtaBreakdown: (r: TimeRangeValue) => request<CtaBreakdownResponse>(`/cta-breakdown?${rangeQuery(r)}`),
  getUtmBreakdown: (r: TimeRangeValue) => request<UtmBreakdownResponse>(`/utm-breakdown?${rangeQuery(r)}`),
  getCampaigns:    () => request<CampaignsResponse>('/campaigns'),
  exportCsvUrl:    (r: TimeRangeValue) => `${BASE}/export.csv?${rangeQuery(r)}`,
};

export default googleAdsLandingApi;
